import { useMemo } from 'react';

import { useCurrentCurrency } from '../../shared/hooks/use-current-currency';
import type { IWallet } from '../store/account.store';
import { WalletType } from '../store/account.store';

function getTotalBalanceOfWallets(wallets: IWallet[]) {
    return (
        wallets.reduce((total, current) => {
            return total + (current?.balanceAmount ?? 0);
        }, 0) ?? 0
    );
}

export function useWalletTotals(wallets?: IWallet[]) {
    const currentCurrency = useCurrentCurrency();
    const applicableWallets = useMemo(() => {
        return (
            wallets?.filter((wallet) => {
                return wallet.currencyCode === currentCurrency && !!wallet?.isActive;
            }) ?? []
        );
    }, [currentCurrency, wallets]);

    const bonusBalanceValue = useMemo(() => {
        return getTotalBalanceOfWallets(
            applicableWallets.filter((item) => item?.type === WalletType.BONUS),
        );
    }, [applicableWallets]);

    const mainBalanceValue = useMemo(() => {
        return getTotalBalanceOfWallets(
            applicableWallets.filter((item) => item && item.type === WalletType.MAIN),
        );
    }, [applicableWallets]);

    const lockedBalanceValue = useMemo(() => {
        return getTotalBalanceOfWallets(
            applicableWallets.filter((item) => {
                return item?.type === WalletType.LOCKED;
            }),
        );
    }, [applicableWallets]);

    const restrictedMainBalance = useMemo(() => {
        return (
            applicableWallets
                .filter((item) => item && item.type === WalletType.MAIN)
                .reduce((total, current) => {
                    const totalRestrictedAmount = (
                        current?.restrictions?.monetaryDebit ?? []
                    ).reduce((restricted, restriction) => {
                        return restricted + (restriction.amount ?? 0);
                    }, 0);

                    return total + Math.min(current.balanceAmount ?? 0, totalRestrictedAmount);
                }, 0) ?? 0
        );
    }, [applicableWallets]);

    const withdrawable = Math.max(0, mainBalanceValue - restrictedMainBalance);

    return useMemo(
        () => ({
            locked: {
                currency: currentCurrency,
                value: lockedBalanceValue + restrictedMainBalance,
            },
            withdrawable: {
                currency: currentCurrency,
                value: withdrawable,
            },
            bonus: {
                currency: currentCurrency,
                value: bonusBalanceValue,
            },
            total: {
                currency: currentCurrency,
                value:
                    withdrawable + bonusBalanceValue + lockedBalanceValue + restrictedMainBalance,
            },
        }),
        [
            currentCurrency,
            restrictedMainBalance,
            withdrawable,
            lockedBalanceValue,
            bonusBalanceValue,
        ],
    );
}
